import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import {
  mockOverview, mockResidency, mockCompliance, mockPia, useMock,
} from '@/api/mock';
import type {
  DashboardOverview, ResidencyRow, ComplianceCard, PiaItem,
} from '@/api/client';
import { SecurityScoreGauge } from '@/components/dashboard/SecurityScoreGauge';
import { VulnerabilitySummary } from '@/components/dashboard/VulnerabilitySummary';
import { RbacAuditSummary } from '@/components/dashboard/RbacAuditSummary';
import { DataResidencyMap } from '@/components/dashboard/DataResidencyMap';
import { ComplianceReportingHub } from '@/components/dashboard/ComplianceReportingHub';
import { PiaTracker } from '@/components/dashboard/PiaTracker';

const DASHBOARD_CANVAS_WIDTH = 1128;

export function DashboardPage() {
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [residency, setResidency] = useState<ResidencyRow[]>([]);
  const [cards, setCards] = useState<ComplianceCard[]>([]);
  const [pia, setPia] = useState<PiaItem[]>([]);
  const frameRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [canvas, setCanvas] = useState({ scale: 1, height: 0 });

  useEffect(() => {
    if (useMock()) {
      setOverview(mockOverview);
      setResidency(mockResidency);
      setCards(mockCompliance);
      setPia(mockPia);
      return;
    }
    api.dashboardOverview().then(setOverview).catch(() => setOverview(mockOverview));
    api.residencyMap().then(setResidency).catch(() => setResidency(mockResidency));
    api.complianceList().then(setCards).catch(() => setCards(mockCompliance));
    api.piaList().then(setPia).catch(() => setPia(mockPia));
  }, []);

  useLayoutEffect(() => {
    const updateCanvas = () => {
      const frameWidth = frameRef.current?.clientWidth ?? DASHBOARD_CANVAS_WIDTH;
      const scale = frameWidth / DASHBOARD_CANVAS_WIDTH;
      const height = (canvasRef.current?.scrollHeight ?? 0) * scale;
      setCanvas((current) => (
        Math.abs(current.scale - scale) < 0.001 && Math.abs(current.height - height) < 1
          ? current
          : { scale, height }
      ));
    };

    updateCanvas();
    const observer = new ResizeObserver(updateCanvas);
    if (frameRef.current) observer.observe(frameRef.current);
    if (canvasRef.current) observer.observe(canvasRef.current);
    window.addEventListener('resize', updateCanvas);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateCanvas);
    };
  }, [overview, residency.length, cards.length, pia.length]);

  if (!overview) return <div className="p-8 text-ink-muted">Loading...</div>;

  return (
    <div
      ref={frameRef}
      className="dashboard-reference w-full overflow-visible"
      style={{ height: canvas.height || undefined }}
    >
      <div
        ref={canvasRef}
        className="w-[1128px] space-y-2.5"
        style={{ transform: `scale(${canvas.scale})`, transformOrigin: 'top left' }}
      >
        <div className="grid grid-cols-[360px_400px_360px] gap-1.5">
          <section className="card dashboard-panel p-2.5">
            <div className="dashboard-title mb-2.5">Security Posture Matrix</div>
            <div className="space-y-2">
              <SecurityScoreGauge score={overview.securityScore} trend30d={overview.scoreTrendDays30} compact />
              <VulnerabilitySummary items={overview.vulnerabilities} compact />
              <RbacAuditSummary {...overview.rbac} compact />
            </div>
          </section>

          <section className="min-w-0">
            <DataResidencyMap rows={residency} globalCompliancePct={overview.globalCompliancePct} />
          </section>

          <section className="min-w-0">
            <ComplianceReportingHub cards={cards} />
          </section>
        </div>

        <PiaTracker items={pia} />
      </div>
    </div>
  );
}
